/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { supabase } from './supabase';
import React, { useState } from 'react';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHIFT_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];
const DEFAULT_ROLES = ['Cook', 'Server', 'Bartender', 'Host', 'Dishwasher', 'Manager'];
const CERT_OPTIONS = ['Food Handler Card', 'Alcohol Certification', 'Bar Certified', 'Manager Certified', 'First Aid'];

const colorOptions = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700',
  'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700',
  'bg-amber-100 text-amber-700', 'bg-purple-100 text-purple-700',
  'bg-teal-100 text-teal-700', 'bg-pink-100 text-pink-700',
  'bg-gray-100 text-gray-700',
];

const colorDots = {
  'bg-blue-100 text-blue-700': '#185FA5',
  'bg-green-100 text-green-700': '#639922',
  'bg-orange-100 text-orange-700': '#C2500A',
  'bg-red-100 text-red-700': '#A32D2D',
  'bg-amber-100 text-amber-700': '#BA7517',
  'bg-purple-100 text-purple-700': '#534AB7',
  'bg-teal-100 text-teal-700': '#0F6E56',
  'bg-pink-100 text-pink-700': '#993556',
  'bg-gray-100 text-gray-700': '#5F5E5A',
};

function calcShiftHours(start, end) {
  if (!start || !end) return 7;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let h = (eh * 60 + em - sh * 60 - sm) / 60;
  if (h < 0) h += 24;
  return Math.round(h * 10) / 10;
}

export default function Employees({ companyId }) {
  const [employeeList, setEmployeeList] = useState([]);
  const [dynamicRoles, setDynamicRoles] = useState(DEFAULT_ROLES);
  const [selected, setSelected] = useState(null);
  const [weekShifts, setWeekShifts] = useState([]);

  // Add employee modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState({});
  const [inviteSent, setInviteSent] = useState(false);

  // Edit profile modal
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editRoles, setEditRoles] = useState([]);
  const [editCerts, setEditCerts] = useState([]);
  const [editDays, setEditDays] = useState([]);
  const [editUnavailable, setEditUnavailable] = useState([]);
  const [unavailExpanded, setUnavailExpanded] = useState(null);

  // Edit availability modal
  const [showEditAvail, setShowEditAvail] = useState(false);
  const [editAvail, setEditAvail] = useState([]);

  // Add recurring shift modal
  const [showAddRecurring, setShowAddRecurring] = useState(false);
  const [recurringDay, setRecurringDay] = useState('Mon');
  const [recurringShift, setRecurringShift] = useState('Dinner');
  const [recurringRole, setRecurringRole] = useState('');
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  React.useEffect(() => {
    async function fetchAll() {
      if (!companyId) return;
      const { data } = await supabase.from('employees').select('*').eq('company_id', companyId);
      if (data && data.length > 0) {
        setEmployeeList(data);
        setSelected(data[0]);
        loadWeekShifts(data[0].id);
      }
      const { data: rolesData } = await supabase.from('roles').select('name').eq('company_id', companyId).order('sort_order');
      if (rolesData && rolesData.length > 0) setDynamicRoles(rolesData.map(r => r.name));
    }
    fetchAll();
  }, [companyId]);

  async function loadWeekShifts(empId) {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now);
    monday.setDate(diff);
    const dates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toISOString().split('T')[0];
    });
    const { data } = await supabase.from('shifts').select('*').eq('employee_id', empId).in('day', dates);
    setWeekShifts(data || []);
  }

  async function refreshEmployees() {
    const { data } = await supabase.from('employees').select('*').eq('company_id', companyId);
    if (data) {
      setEmployeeList(data);
      const updated = data.find(e => e.id === selected?.id);
      if (updated) { setSelected(updated); loadWeekShifts(updated.id); }
    }
  }

  async function selectEmployee(emp) {
    setSelected(emp);
    loadWeekShifts(emp.id);
  }

  function openEditProfile() {
    const currentRoles = typeof selected.role === 'string'
      ? selected.role.split(',').map(r => r.trim()).filter(Boolean)
      : (selected.role || []);
    const currentCerts = (selected.certifications || '').split(',').map(c => c.trim()).filter(Boolean);
    const currentDays = (selected.preferred_days || '').split(',').map(d => d.trim()).filter(Boolean);
    setEditRoles(currentRoles);
    setEditCerts(currentCerts);
    setEditDays(currentDays);
    setEditForm({
      name: selected.name || '',
      phone: selected.phone || '',
      email: selected.email || '',
      target_hours: selected.target_hours || 30,
      max_hours_per_week: selected.max_hours_per_week || 40,
      preferred_shifts: selected.preferred_shifts || selected.preferred_shift || 'Dinner',
      wants_more_hours: selected.wants_more_hours || false,
      hourly_rate: selected.hourly_rate || 0,
      emergency_contact: selected.emergency_contact || '',
      employee_notes: selected.employee_notes || '',
      can_open: selected.can_open !== false,
      can_close: selected.can_close !== false,
      prefers_doubles: selected.prefers_doubles || false,
      no_clopens: selected.no_clopens !== false,
      max_days_per_week: selected.max_days_per_week ?? 6,
      is_minor: selected.is_minor || false,
      primary_role: selected.primary_role || (typeof selected.role === 'string' ? selected.role.split(',').map(r => r.trim()).filter(Boolean)[0] : '') || '',
    });
    setEditUnavailable(Array.isArray(selected.unavailable) ? JSON.parse(JSON.stringify(selected.unavailable)) : []);
    setUnavailExpanded(null);
    setShowEditProfile(true);
  }

  async function saveProfile() {
    await supabase.from('employees').update({
      name: editForm.name,
      phone: editForm.phone,
      email: editForm.email,
      target_hours: parseInt(editForm.target_hours),
      max_hours_per_week: parseInt(editForm.max_hours_per_week),
      preferred_shifts: editForm.preferred_shifts,
      preferred_days: editDays.join(','),
      certifications: editCerts.join(','),
      wants_more_hours: editForm.wants_more_hours,
      hourly_rate: parseFloat(editForm.hourly_rate) || 0,
      emergency_contact: editForm.emergency_contact,
      employee_notes: editForm.employee_notes,
      role: editRoles.join(', '),
      primary_role: editRoles.includes(editForm.primary_role) ? editForm.primary_role : (editRoles[0] || null),
      initials: editForm.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
      can_open: editForm.can_open,
      can_close: editForm.can_close,
      prefers_doubles: editForm.prefers_doubles,
      no_clopens: editForm.no_clopens,
      max_days_per_week: parseInt(editForm.max_days_per_week) || 6,
      is_minor: editForm.is_minor,
      unavailable: editUnavailable.filter(u => u.allDay || (u.periods && u.periods.length > 0)),
    }).eq('id', selected.id);
    setShowEditProfile(false);
    refreshEmployees();
  }

  function openEditAvail() {
    setEditAvail((selected.availability || '').split(',').map(d => d.trim()).filter(Boolean));
    setShowEditAvail(true);
  }

  async function saveAvailability() {
    await supabase.from('employees').update({ availability: editAvail.join(',') }).eq('id', selected.id);
    setShowEditAvail(false);
    refreshEmployees();
  }

  function openAddRecurring() {
    const roles = typeof selected.role === 'string'
      ? selected.role.split(',').map(r => r.trim()).filter(Boolean)
      : (selected.role || []);
    setRecurringRole(roles[0] || dynamicRoles[0] || 'Server');
    setRecurringDay('Mon');
    setRecurringShift('Dinner');
    setShowAddRecurring(true);
  }

  async function saveRecurring() {
    const current = Array.isArray(selected.recurring) ? selected.recurring : [];
    const updated = [...current, { day: recurringDay, shift: recurringShift, role: recurringRole, active: true }];
    const { error } = await supabase.from('employees').update({ recurring: updated }).eq('id', selected.id);
    if (error) { console.error('Error saving recurring:', error); alert('Failed to save recurring shift'); return; }
    setShowAddRecurring(false);
    refreshEmployees();
  }

  if (!selected) return <div className="p-8 text-gray-400">Loading staff...</div>;

  const selectedRoles = typeof selected.role === 'string'
    ? selected.role.split(',').map(r => r.trim())
    : (selected.role || []);

  // Calculate this week's cost
  const weekHours = weekShifts.reduce((acc, s) => acc + calcShiftHours(s.start_time, s.end_time), 0);
  const weekCost = weekHours * (selected.hourly_rate || 0);

  const filteredEmployees = employeeList.filter(emp => {
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!emp.name.toLowerCase().includes(q)) return false;
    }
    if (roleFilter !== 'All') {
      const roles = typeof emp.role === 'string' ? emp.role.split(',').map(r => r.trim()) : (emp.role || []);
      if (!roles.includes(roleFilter)) return false;
    }
    return true;
  });

  return (
    <div className="flex h-full">
      {/* Employee list */}
      <div className="w-56 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">All staff</span>
          <button onClick={() => setShowInvite(true)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700">+ Add</button>
        </div>
        <div className="px-2 pt-2 pb-1 flex flex-col gap-1.5 border-b border-gray-100">
          <input type="text" placeholder="Search name..." value={searchText} onChange={e => setSearchText(e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-gray-50" />
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-gray-50">
            <option value="All">All roles</option>
            {dynamicRoles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredEmployees.map(emp => (
            <button key={emp.id} onClick={() => selectEmployee(emp)}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-center gap-2.5 hover:bg-gray-50 ${selected.id === emp.id ? 'bg-blue-50' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${emp.color}`}>{emp.initials}</div>
              <div>
                <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                <div className="text-xs text-gray-400 truncate">{typeof emp.role === 'string' ? emp.role : (emp.role || []).join(', ')}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Employee detail */}
      <div className="flex-1 overflow-y-auto p-5 bg-gray-50">
        <div className="grid grid-cols-3 gap-4">

          {/* Profile card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Profile</span>
              <button onClick={openEditProfile} className="text-xs text-blue-600 hover:underline">Edit</button>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-medium ${selected.color}`}>{selected.initials}</div>
              <div>
                <div className="text-base font-medium text-gray-900">{selected.name}</div>
                <div className="text-xs text-green-600 mt-0.5">● Active · since {selected.since}</div>
              </div>
            </div>
            {[
              { label: 'Phone', val: selected.phone },
              { label: 'Email', val: selected.email },
              { label: 'Roles', val: selectedRoles.join(', ') },
              { label: 'Target hours', val: `${selected.target_hours} hrs/wk` },
              { label: 'Max hours/wk', val: selected.max_hours_per_week ? `${selected.max_hours_per_week} hrs` : '—' },
              { label: 'Hourly rate', val: selected.hourly_rate ? `$${selected.hourly_rate}/hr` : '—', highlight: true },
              { label: 'Preferred shifts', val: selected.preferred_shifts || selected.preferred_shift || '—' },
              { label: 'Preferred days', val: selected.preferred_days || '—' },
              { label: 'Certifications', val: selected.certifications || '—' },
              { label: 'Emergency contact', val: selected.emergency_contact || '—' },
              { label: 'Wants more hours', val: selected.wants_more_hours ? 'Yes' : 'No' },
              { label: 'Notes', val: selected.employee_notes || '—' },
            ].map((f, i) => (
              <div key={i} className="flex justify-between items-start py-1.5 border-b border-gray-100 last:border-0 gap-2">
                <span className="text-xs text-gray-400 flex-shrink-0">{f.label}</span>
                <span className={`text-xs font-medium text-right min-w-0 break-words ${f.highlight ? 'text-green-700' : 'text-gray-800'}`} style={{ wordBreak: 'break-word' }}>{f.val}</span>
              </div>
            ))}
          </div>

          {/* Availability + This week cost */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Availability</span>
                <button onClick={openEditAvail} className="text-xs text-blue-600 hover:underline">Edit</button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-3">
                {days.map(d => (
                  <div key={d} className="text-center">
                    <div className="text-xs text-gray-400 mb-1">{d}</div>
                    <div className={`h-8 rounded text-xs flex items-center justify-center font-medium ${(selected.availability || '').includes(d) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-300'}`}>
                      {(selected.availability || '').includes(d) ? '✓' : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* This week labor cost */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">This week</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xs text-blue-500 mb-1">Hours scheduled</div>
                  <div className="text-xl font-medium text-blue-800">{weekHours.toFixed(1)}</div>
                  <div className="text-xs text-blue-400">of {selected.target_hours || 30} target</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-green-500 mb-1">Estimated cost</div>
                  <div className="text-xl font-medium text-green-800">
                    {selected.hourly_rate ? `$${weekCost.toFixed(0)}` : '—'}
                  </div>
                  <div className="text-xs text-green-400">
                    {selected.hourly_rate ? `@ $${selected.hourly_rate}/hr` : 'Set hourly rate'}
                  </div>
                </div>
              </div>
              {weekShifts.length > 0 && (
                <div className="mt-3 flex flex-col gap-1">
                  {weekShifts.map(s => (
                    <div key={s.id} className="flex justify-between items-center text-xs text-gray-500 py-1 border-b border-gray-50 last:border-0">
                      <span>{s.day} · {s.shift_name}</span>
                      <span className="text-gray-700 font-medium">
                        {calcShiftHours(s.start_time, s.end_time)}h
                        {selected.hourly_rate ? ` · $${(calcShiftHours(s.start_time, s.end_time) * selected.hourly_rate).toFixed(0)}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recurring schedule */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Recurring schedule</span>
            </div>
            <div className="text-xs text-gray-400 mb-3">Repeats every week unless overridden.</div>
            <div className="flex flex-col gap-1.5">
              {(selected.recurring || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between px-2.5 py-2 rounded-lg border border-gray-200 bg-white">
                  <div>
                    <span className="text-xs font-medium text-gray-800">{r.day}</span>
                    <span className="text-xs text-gray-400 ml-2">{r.shift}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{r.role}</span>
                    <button onClick={async () => {
                      const updated = (selected.recurring || []).filter((_, idx) => idx !== i);
                      await supabase.from('employees').update({ recurring: updated }).eq('id', selected.id);
                      refreshEmployees();
                    }} className="text-xs text-red-400 hover:text-red-600">✕</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={openAddRecurring} className="text-xs text-blue-600 mt-3 hover:underline">+ Add recurring shift</button>
          </div>
        </div>
      </div>

      {/* EDIT PROFILE MODAL - full */}
      {showEditProfile && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-lg mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-medium text-gray-900">Edit employee profile</div>
              <button onClick={() => setShowEditProfile(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>

            {/* Basic info */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Basic info</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Full name', key: 'name', type: 'text' },
                { label: 'Phone', key: 'phone', type: 'text' },
                { label: 'Email', key: 'email', type: 'email' },
                { label: 'Emergency contact', key: 'emergency_contact', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{f.label}</label>
                  <input type={f.type} value={editForm[f.key] || ''}
                    onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                </div>
              ))}
            </div>

            {/* Roles */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Roles</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {dynamicRoles.map(role => (
                <button key={role} onClick={() => setEditRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role])}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${editRoles.includes(role) ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'}`}>
                  {role}
                </button>
              ))}
            </div>
            {editRoles.length > 1 && (
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Primary role <span className="text-gray-400 font-normal">(decides which section they appear in on By Role view)</span></label>
                <select value={editForm.primary_role || editRoles[0] || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, primary_role: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50">
                  {editRoles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            {/* Scheduling */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Scheduling</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Target hrs/wk</label>
                <input type="number" value={editForm.target_hours || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, target_hours: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Max hrs/wk</label>
                <input type="number" value={editForm.max_hours_per_week || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, max_hours_per_week: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Preferred shifts</label>
              <div className="grid grid-cols-4 gap-1">
                {SHIFT_TYPES.map(s => {
                  const prefs = (editForm.preferred_shifts || '').split(',').map(p => p.trim()).filter(Boolean);
                  const active = prefs.includes(s);
                  return (
                    <button key={s} onClick={() => {
                      const newPrefs = active ? prefs.filter(p => p !== s) : [...prefs, s];
                      setEditForm(prev => ({ ...prev, preferred_shifts: newPrefs.join(',') }));
                    }}
                      className={`py-1.5 rounded-lg text-xs font-medium border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preferred days */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-2">Preferred days</label>
              <div className="grid grid-cols-7 gap-1">
                {days.map(d => (
                  <button key={d} onClick={() => setEditDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                    className={`py-1.5 rounded-lg text-xs font-medium border ${editDays.includes(d) ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Scheduling Preferences */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3 mt-2">Scheduling preferences</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { key: 'can_open', label: 'Can open', desc: 'Trained to open' },
                { key: 'can_close', label: 'Can close', desc: 'Trained to close' },
                { key: 'prefers_doubles', label: 'Prefers doubles', desc: 'Likes Lunch + Dinner' },
                { key: 'no_clopens', label: 'No clopens', desc: 'No close then open' },
                { key: 'is_minor', label: 'Minor (under 18)', desc: 'Labor law limits' },
                { key: 'wants_more_hours', label: 'Wants more hours', desc: 'Prioritize for shifts' },
              ].map(({ key, label, desc }) => (
                <button key={key} onClick={() => setEditForm(prev => ({ ...prev, [key]: !prev[key] }))}
                  className={`text-left px-3 py-2 rounded-lg border text-xs ${editForm[key] ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className={`font-medium ${editForm[key] ? 'text-blue-700' : 'text-gray-500'}`}>{editForm[key] ? '\u2713 ' : ''}{label}</div>
                  <div className="text-gray-400 mt-0.5" style={{ fontSize: 10 }}>{desc}</div>
                </button>
              ))}
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Max days per week</label>
              <input type="number" min="1" max="7" value={editForm.max_days_per_week ?? 6}
                onChange={e => setEditForm(prev => ({ ...prev, max_days_per_week: e.target.value }))}
                className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
            </div>

            {/* Certifications */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-2">Certifications</label>
              <div className="flex flex-wrap gap-2">
                {CERT_OPTIONS.map(c => (
                  <button key={c} onClick={() => setEditCerts(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium ${editCerts.includes(c) ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Unavailability */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2 mt-2">Unavailability (manager only)</div>
            <div className="mb-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {days.map(d => {
                  const entry = editUnavailable.find(u => u.day === d);
                  const isBlocked = entry?.allDay;
                  const isPartial = entry && !entry.allDay && entry.periods?.length > 0;
                  return (
                    <button key={d} onClick={() => setUnavailExpanded(unavailExpanded === d ? null : d)}
                      className={`py-1.5 rounded-lg text-xs font-medium border relative ${isBlocked ? 'bg-red-100 text-red-600 border-red-200' : isPartial ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}
                      style={{ textDecoration: isBlocked ? 'line-through' : 'none' }}>
                      {d}
                      {isPartial && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400" />}
                    </button>
                  );
                })}
              </div>
              {unavailExpanded && (() => {
                const d = unavailExpanded;
                const entry = editUnavailable.find(u => u.day === d) || { day: d, allDay: false, periods: [] };
                const setEntry = (updates) => {
                  setEditUnavailable(prev => {
                    const existing = prev.findIndex(u => u.day === d);
                    const newEntry = { ...entry, ...updates };
                    if (existing >= 0) { const n = [...prev]; n[existing] = newEntry; return n; }
                    return [...prev, newEntry];
                  });
                };
                const togglePeriod = (period) => {
                  const periods = entry.periods || [];
                  setEntry({ allDay: false, periods: periods.includes(period) ? periods.filter(p => p !== period) : [...periods, period] });
                };
                return (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700">{d}</span>
                      <button onClick={() => setUnavailExpanded(null)} className="text-xs text-gray-400">{'\u2715'}</button>
                    </div>
                    <button onClick={() => setEntry({ allDay: !entry.allDay, periods: [] })}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-xs mb-2 ${entry.allDay ? 'bg-red-50 border-red-200 text-red-600 font-medium' : 'bg-white border-gray-200 text-gray-500'}`}>
                      {entry.allDay ? '\u2713 ' : ''}Block entire day
                    </button>
                    {!entry.allDay && (
                      <div className="grid grid-cols-4 gap-1">
                        {SHIFT_TYPES.map(p => {
                          const blocked = (entry.periods || []).includes(p);
                          return (
                            <button key={p} onClick={() => togglePeriod(p)}
                              className={`py-1.5 rounded-lg text-xs font-medium border ${blocked ? 'bg-red-100 text-red-600 border-red-200' : 'bg-white text-gray-400 border-gray-200'}`}>
                              {blocked ? '\u2715 ' : ''}{p}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              {editUnavailable.filter(u => u.allDay || (u.periods && u.periods.length > 0)).length > 0 && (
                <div className="text-xs text-gray-400">
                  Blocked: {editUnavailable.filter(u => u.allDay || (u.periods && u.periods.length > 0)).map(u => `${u.day} (${u.allDay ? 'all day' : (u.periods || []).join(', ')})`).join(', ')}
                </div>
              )}
            </div>

            {/* Pay & notes */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Pay & notes</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Hourly rate ($)</label>
                <input type="number" step="0.25" value={editForm.hourly_rate || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                  placeholder="e.g. 15.00"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
              </div>
              <div className="flex items-center gap-2 mt-5">
                <input type="checkbox" checked={editForm.wants_more_hours || false}
                  onChange={e => setEditForm(prev => ({ ...prev, wants_more_hours: e.target.checked }))} />
                <label className="text-xs text-gray-600">Wants more hours</label>
              </div>
            </div>
            <div className="mb-5">
              <label className="text-xs font-medium text-gray-500 block mb-1">Manager notes (private)</label>
              <textarea value={editForm.employee_notes || ''} rows={2}
                onChange={e => setEditForm(prev => ({ ...prev, employee_notes: e.target.value }))}
                placeholder="Notes only visible to managers..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none" />
            </div>

            <button onClick={saveProfile}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700">
              Save changes
            </button>
          </div>
        </div>
      )}

      {/* EDIT AVAILABILITY MODAL */}
      {showEditAvail && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-medium text-gray-900">Edit availability</div>
              <button onClick={() => setShowEditAvail(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="grid grid-cols-7 gap-2 mb-6">
              {days.map(d => (
                <button key={d} onClick={() => setEditAvail(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                  className={`py-2 rounded-lg text-xs font-medium border ${editAvail.includes(d) ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                  {d}
                </button>
              ))}
            </div>
            <button onClick={saveAvailability}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700">Save availability</button>
          </div>
        </div>
      )}

      {/* ADD RECURRING SHIFT MODAL */}
      {showAddRecurring && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-medium text-gray-900">Add recurring shift</div>
              <button onClick={() => setShowAddRecurring(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 block mb-1">Day</label>
              <div className="grid grid-cols-7 gap-1">
                {days.map(d => (
                  <button key={d} onClick={() => setRecurringDay(d)}
                    className={`py-1.5 rounded-lg text-xs font-medium border ${recurringDay === d ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 block mb-1">Shift</label>
              <select value={recurringShift} onChange={e => setRecurringShift(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50">
                {SHIFT_TYPES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="mb-5">
              <label className="text-xs font-medium text-gray-500 block mb-1">Role</label>
              <select value={recurringRole} onChange={e => setRecurringRole(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50">
                {(selectedRoles.length > 0 ? selectedRoles : dynamicRoles).map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <button onClick={saveRecurring}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700">Add recurring shift</button>
          </div>
        </div>
      )}

      {/* ADD EMPLOYEE MODAL */}
      {showInvite && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-sm mx-4 max-h-screen overflow-y-auto">
            {inviteSent ? (
              <div className="text-center py-4">
                <div className="text-3xl mb-3">✓</div>
                <div className="text-base font-medium text-gray-900 mb-1">Employee added!</div>
                <div className="text-sm text-gray-400 mb-4">{inviteName} has been added to your staff.</div>
                <button onClick={() => { setShowInvite(false); setInviteSent(false); setInviteEmail(''); setInviteName(''); setInviteRole({}); refreshEmployees(); }}
                  className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700">Done</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div className="text-base font-medium text-gray-900">Add employee</div>
                  <button onClick={() => setShowInvite(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
                </div>
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Full name</label>
                  <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                </div>
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Email</label>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder="jane@email.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                </div>
                <div className="mb-6">
                  <label className="text-xs font-medium text-gray-500 block mb-2">Roles & skill level</label>
                  {dynamicRoles.map(role => (
                    <div key={role} className="mb-2">
                      <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={!!inviteRole[role]}
                            onChange={e => {
                              if (e.target.checked) setInviteRole(prev => ({ ...prev, [role]: 1 }));
                              else setInviteRole(prev => { const next = { ...prev }; delete next[role]; return next; });
                            }} className="w-3.5 h-3.5" />
                          <span className="text-sm text-gray-700">{role}</span>
                        </div>
                        {inviteRole[role] && (
                          <div className="flex gap-1">
                            {[1, 2, 3].map(n => (
                              <button key={n} type="button" onClick={() => setInviteRole(prev => ({ ...prev, [role]: n }))}
                                className={`w-7 h-7 rounded-lg text-xs font-medium border ${inviteRole[role] === n ? n === 1 ? 'bg-red-100 text-red-700 border-red-200' : n === 2 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200' : 'bg-white text-gray-300 border-gray-200'}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={async () => {
                  if (!inviteEmail || !inviteName || !Object.keys(inviteRole).length) return;
                  await supabase.from('employees').insert([{
                    name: inviteName, email: inviteEmail,
                    role: Object.keys(inviteRole).join(', '),
                    initials: inviteName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
                    color: colorOptions[Math.floor(Math.random() * colorOptions.length)],
                    status: 'active', target_hours: 30,
                    since: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                    availability: 'Mon,Tue,Wed,Thu,Fri',
                    preferred_shift: 'Dinner', wants_more_hours: false,
                    company_id: companyId,
                  }]);
                  setInviteSent(true);
                }} className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700">
                  Add employee
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}